/**
 * @fileoverview Display helpers for the department-wide occupancy stepper.
 * @module system-web/services/departmentWideStepper
 *
 * Responsibilities:
 * - Group adapter steps into Foundations → Documents tiers.
 * - Flag completed steps that still have row-level failures (amber).
 * - Convert recognized migration internals into actionable administrator messages.
 *
 * Dependencies: none (pure). Must stay in sync with api/packages/legacy-migration departmentWide.ts.
 *
 * Architecture: Web copy of the frozen DAG display rules — system-web cannot import the API package.
 *
 * Notes: Raw diagnostic errors remain persisted server-side; this module changes presentation only.
 *
 * @localFirst
 * Source of Truth: Dexie run.steps (already loaded by the hook)
 * Write Flow: none
 * Sync Strategy: N/A
 * Conflict Resolution: N/A
 */

export const DEPARTMENT_WIDE_TIERS: ReadonlyArray<{ id: string; label: string; adapterKeys: readonly string[] }> = [
  { id: 'foundations', label: 'Foundations', adapterKeys: ['referenceLookups', 'udfBlueprints'] },
  { id: 'buildings', label: 'Buildings', adapterKeys: ['inspectableEntities'] },
  { id: 'people', label: 'People & notes', adapterKeys: ['occupancyContacts', 'buildingInformationalItems', 'communicationLogs'] },
  { id: 'systems', label: 'Systems', adapterKeys: ['buildingSystems'] },
  { id: 'hazmat', label: 'Hazmat & tanks', adapterKeys: ['masterChemicals', 'occupancyChemicals', 'occupancyTanks'] },
  { id: 'admin', label: 'Admin', adapterKeys: ['occupancyPermits', 'riskProfiles', 'tceCompliance'] },
  { id: 'hydrants', label: 'Hydrants', adapterKeys: ['hydrantMain'] },
  { id: 'crr', label: 'Community Risk', adapterKeys: ['crrProfile', 'crrDetector', 'crrDetectorEvent'] },
  { id: 'documents', label: 'Documents', adapterKeys: ['fileMetadata'] },
];

/**
 * @reusable
 * Usage: MigrationProgressSidebar grouping.
 * Rules: presentation-only; leftover keys go under Other.
 */
export function groupDepartmentWideStepsByTier<T extends { adapterKey: string }>(
  steps: readonly T[],
): Array<{ id: string; label: string; steps: T[] }> {
  const byKey = new Map(steps.map((s) => [s.adapterKey, s]));
  const used = new Set<string>();
  const groups: Array<{ id: string; label: string; steps: T[] }> = [];
  for (const tier of DEPARTMENT_WIDE_TIERS) {
    const bucket = tier.adapterKeys
      .map((key) => byKey.get(key))
      .filter((s): s is T => Boolean(s));
    if (bucket.length === 0) continue;
    for (const s of bucket) used.add(s.adapterKey);
    groups.push({ id: tier.id, label: tier.label, steps: bucket });
  }
  const leftover = steps.filter((s) => !used.has(s.adapterKey));
  if (leftover.length > 0) {
    groups.push({ id: 'other', label: 'Other', steps: leftover as T[] });
  }
  return groups;
}

/**
 * @reusable
 * Usage: Amber badge when a completed step still has row failures.
 */
export function isCompletedWithRowFailures(step: { status: string; counts?: { failed?: number } }): boolean {
  return step.status === 'completed' && (step.counts?.failed ?? 0) > 0;
}

export interface RehostCounts {
  copied: number;
  pending: number;
  failed: number;
  skipped?: number;
  total: number;
}

/**
 * @contract OverlayableChildRun
 * Purpose: Latest single-dataset re-run used to paint live counts on a frozen
 *          department-wide step card without rewriting that historical report.
 * Fields:  status, counts, processed, total, phase, message, heartbeatAt, overallPercent, _id
 * Rules:   Latest child attempt drives live, completed, failed, or cancelled effective status without mutating history.
 * Sync:    Dexie mirror of legacyMigrationRuns; parent steps stay server-authoritative history.
 */
export interface OverlayableChildRun {
  _id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  counts?: { selected?: number; created?: number; updated?: number; skipped?: number; failed?: number };
  processed?: number;
  total?: number;
  phase?: 'extracting' | 'writing';
  message?: string;
  heartbeatAt?: string;
  overallPercent?: number;
  startedAt?: string;
  cancelRequested?: boolean;
  failedRows?: Array<{ legacyId: string; error: string }>;
  failedReasonSummary?: Array<{ error: string; count: number }>;
  rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
  rehostCounts?: RehostCounts;
}

/**
 * @reusable
 * Usage: Department-wide report tiles while a Hydrants (or other) re-run is in flight.
 * Rules: Does not mutate the parent run. Live/newer child counts replace the frozen step line.
 */
export function overlayStepWithChildRun<T extends {
  adapterKey: string;
  status: string;
  percent: number;
  counts: { selected: number; created: number; updated: number; skipped: number; failed: number };
  processed?: number;
  total?: number;
  phase?: 'extracting' | 'writing';
  message?: string;
  heartbeatAt?: string;
  failedRows?: OverlayableChildRun['failedRows'];
  failedReasonSummary?: OverlayableChildRun['failedReasonSummary'];
}>(step: T, child: OverlayableChildRun | null | undefined): T & {
  overlayRunId?: string;
  overlayStartedAt?: string;
  failedRows?: OverlayableChildRun['failedRows'];
  failedReasonSummary?: OverlayableChildRun['failedReasonSummary'];
} {
  if (!child) return step;
  const cancelled = child.status === 'cancelled' || Boolean(child.cancelRequested);
  const live = !cancelled && (child.status === 'running' || child.status === 'pending');
  const childCounts = child.counts;
  if (child.status === 'failed') {
    return {
      ...step,
      status: 'failed',
      message: child.message,
      heartbeatAt: child.heartbeatAt,
      overlayRunId: child._id,
      overlayStartedAt: child.startedAt,
      failedRows: child.failedRows,
      failedReasonSummary: child.failedReasonSummary,
    };
  }
  if (child.status === 'completed' && childCounts) {
    const replacesWholeDataset = (childCounts.selected ?? child.total ?? 0) >= step.counts.selected;
    const retriedCount = childCounts.selected ?? child.total ?? 0;
    const counts = replacesWholeDataset
      ? {
          selected: step.counts.selected,
          created: childCounts.created ?? 0,
          updated: childCounts.updated ?? 0,
          skipped: childCounts.skipped ?? 0,
          failed: childCounts.failed ?? 0,
        }
      : {
          selected: step.counts.selected,
          created: step.counts.created + (childCounts.created ?? 0),
          updated: step.counts.updated + (childCounts.updated ?? 0),
          skipped: step.counts.skipped + (childCounts.skipped ?? 0),
          failed: Math.max(0, step.counts.failed - retriedCount) + (childCounts.failed ?? 0),
        };
    const failedRows = retriedCount === 0
      ? step.failedRows
      : child.failedRows?.length
        ? child.failedRows
        : (childCounts.failed ?? 0) === 0 ? [] : step.failedRows;
    const failedReasonSummary = retriedCount === 0
      ? step.failedReasonSummary
      : child.failedReasonSummary?.length
        ? child.failedReasonSummary
        : (childCounts.failed ?? 0) === 0 ? [] : step.failedReasonSummary;
    return {
      ...step,
      status: 'completed',
      percent: 100,
      counts,
      processed: child.processed ?? childCounts.selected,
      total: child.total ?? childCounts.selected,
      phase: child.phase,
      message: child.message,
      heartbeatAt: child.heartbeatAt,
      overlayRunId: child._id,
      overlayStartedAt: child.startedAt,
      failedRows,
      failedReasonSummary,
    };
  }
  if (!live && !cancelled) return step;
  return {
    ...step,
    status: cancelled ? 'cancelled' : child.status,
    percent: typeof child.overallPercent === 'number'
      ? child.overallPercent
      : live ? Math.max(step.percent, 5) : step.percent,
    counts: {
      selected: childCounts?.selected ?? step.counts.selected,
      created: childCounts?.created ?? 0,
      updated: childCounts?.updated ?? 0,
      skipped: childCounts?.skipped ?? 0,
      failed: childCounts?.failed ?? 0,
    },
    processed: child.processed,
    total: child.total,
    phase: child.phase,
    message: child.message,
    heartbeatAt: child.heartbeatAt,
    overlayRunId: child._id,
    overlayStartedAt: child.startedAt,
    failedRows: child.failedRows,
    failedReasonSummary: child.failedReasonSummary,
  };
}

/**
 * @reusable
 * Usage: Run dashboard when a dataset has multiple rerun attempts.
 * Rules: Applies attempts oldest-to-newest so targeted retries merge into the latest full-dataset result.
 */
export function overlayStepWithChildRuns<T extends Parameters<typeof overlayStepWithChildRun>[0]>(
  step: T,
  children: readonly OverlayableChildRun[],
): ReturnType<typeof overlayStepWithChildRun<T>> {
  return [...children]
    .sort((a, b) => String(a.startedAt ?? '').localeCompare(String(b.startedAt ?? '')))
    .reduce((effective, child) => overlayStepWithChildRun(effective, child), step) as ReturnType<typeof overlayStepWithChildRun<T>>;
}

/**
 * @reusable
 * Usage: Department dashboard / header copy line after a Documents tab rerun.
 * Rules: Latest child attempt with rehost fields replaces frozen parent rehostStatus.
 *        Historical parent run document is not mutated.
 */
export function overlayRehostFromChildRuns(
  parent: {
    rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
    rehostCounts?: RehostCounts;
  },
  children: ReadonlyArray<OverlayableChildRun | null | undefined>,
): {
  rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
  rehostCounts?: RehostCounts;
} {
  const sorted = [...children]
    .filter((child): child is OverlayableChildRun => Boolean(child && (child.rehostStatus || child.rehostCounts)))
    .sort((a, b) => String(a.startedAt ?? '').localeCompare(String(b.startedAt ?? '')));
  const latest = sorted[sorted.length - 1];
  if (!latest) {
    return { rehostStatus: parent.rehostStatus, rehostCounts: parent.rehostCounts };
  }
  return {
    rehostStatus: latest.rehostStatus ?? parent.rehostStatus,
    rehostCounts: latest.rehostCounts ?? parent.rehostCounts,
  };
}

/**
 * @contract EffectiveDepartmentMigrationSummary
 * Purpose: Run-centric status derived from the latest effective result for every department dataset.
 * Fields: status, counts, completedDatasets, remainingDatasetKeys, failedRows.
 * Rules: Latest rerun overlays are supplied by the caller; historical parent steps remain unchanged.
 * Sync: Pure derivation from Dexie-backed parent and child run records.
 */
export interface EffectiveDepartmentMigrationSummary {
  status: 'running' | 'completed' | 'needs-attention';
  counts: { selected: number; created: number; updated: number; skipped: number; failed: number };
  completedDatasets: number;
  remainingDatasetKeys: string[];
  failedRows: number;
}

/**
 * @reusable
 * Usage: Department migration dashboard and compact header card.
 * Rules: Sums effective step counts and treats any non-completed or failed-row step as remaining work.
 */
export function summarizeEffectiveDepartmentMigration(
  steps: ReadonlyArray<{
    adapterKey: string;
    status: string;
    counts: { selected: number; created: number; updated: number; skipped: number; failed: number };
  }>,
): EffectiveDepartmentMigrationSummary {
  const counts = steps.reduce((sum, step) => ({
    selected: sum.selected + step.counts.selected,
    created: sum.created + step.counts.created,
    updated: sum.updated + step.counts.updated,
    skipped: sum.skipped + step.counts.skipped,
    failed: sum.failed + step.counts.failed,
  }), { selected: 0, created: 0, updated: 0, skipped: 0, failed: 0 });
  const remainingDatasetKeys = steps
    .filter((step) => step.status !== 'completed' || step.counts.failed > 0)
    .map((step) => step.adapterKey);
  const running = steps.some((step) => step.status === 'running' || step.status === 'pending');
  return {
    status: running ? 'running' : remainingDatasetKeys.length > 0 ? 'needs-attention' : 'completed',
    counts,
    completedDatasets: steps.length - remainingDatasetKeys.length,
    remainingDatasetKeys,
    failedRows: counts.failed,
  };
}

const DOCUMENT_TRANSFER_LEDGER_CONFLICT = /Updating the path ['"]attempts['"] would create a conflict at ['"]attempts['"]/i;
const DOCUMENT_ATTACHMENT_ARRAY_MISSING = /The path ['"]attachments['"] must exist in the document in order to apply array updates/i;
const DOCUMENT_TRANSFER_LEDGER_MESSAGE = 'Document metadata was saved, but the file could not be queued for transfer. Retry the Documents migration. If it fails again, contact support with the run ID.';
const DOCUMENT_ATTACHMENT_ARRAY_MESSAGE = 'Document metadata could not be added to its related record. Retry the Documents migration. If it fails again, contact support with the run ID.';

/**
 * @reusable
 * Usage: Run history and report views before rendering a persisted migration failure.
 * Rules: Presentation-only; translates only recognized technical signatures and preserves all unknown errors unchanged.
 */
export function formatMigrationFailureForAdmin(error: string | undefined): string {
  const normalized = (error ?? 'Unknown error').replace(/\s+/g, ' ').trim() || 'Unknown error';
  if (DOCUMENT_TRANSFER_LEDGER_CONFLICT.test(normalized)) return DOCUMENT_TRANSFER_LEDGER_MESSAGE;
  if (DOCUMENT_ATTACHMENT_ARRAY_MISSING.test(normalized)) return DOCUMENT_ATTACHMENT_ARRAY_MESSAGE;
  return normalized;
}

/**
 * @reusable
 * Usage: Occupancies Run History — group byRow / failedRows into count + administrator-readable reason.
 * Rules: Presentation-only; known errors are translated before grouping and unknown errors retain diagnostic text.
 */
export function summarizeFailedRows(
  rows: ReadonlyArray<{ action?: string; error?: string }>,
): Array<{ error: string; count: number }> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.action && row.action !== 'failed') continue;
    const error = formatMigrationFailureForAdmin(row.error);
    counts.set(error, (counts.get(error) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([error, count]) => ({ error, count }))
    .sort((a, b) => b.count - a.count);
}

export const STEP_HEARTBEAT_STALE_MS = 120_000;

/**
 * @reusable
 * Usage: Sidebar line — "12 / 500 updating" or extract wait text.
 * Rules: presentation-only.
 */
export function formatStepProgressLine(step: {
  status?: string;
  phase?: string;
  processed?: number;
  total?: number;
  message?: string;
  displayName?: string;
}): string {
  const total = step.total ?? 0;
  if (total > 0) {
    const message = step.message?.trim();
    if (step.phase === 'writing' && message && !message.startsWith('Reading ')) return message;
    const verb = step.phase === 'extracting' ? 'extracted' : 'updating';
    return `${step.processed ?? 0} / ${total} ${verb}`;
  }
  if (step.message?.trim()) return step.message.trim();
  if (step.phase === 'extracting') {
    return `Reading ${step.displayName ?? 'records'} from Streamline SQL…`;
  }
  return '';
}

/**
 * @reusable
 * Usage: Warn when a running step has not heartbeated for 2 minutes.
 */
export function isStepHeartbeatStale(
  heartbeatAt: Date | string | undefined,
  nowMs: number = Date.now(),
  staleMs: number = STEP_HEARTBEAT_STALE_MS,
  startedAt?: Date | string,
): boolean {
  const reference = heartbeatAt ?? startedAt;
  if (!reference) return false;
  const t = reference instanceof Date ? reference.getTime() : new Date(reference).getTime();
  if (!Number.isFinite(t)) return false;
  return nowMs - t > staleMs;
}

/**
 * @reusable
 * Usage: Single-dataset report page activity guard when a worker has not emitted its first heartbeat.
 * Rules: Presentation-only; live runs use heartbeatAt, then updatedAt, then startedAt as the activity reference.
 */
export function isMigrationRunHeartbeatStale(
  run: { status: string; heartbeatAt?: Date | string; updatedAt?: Date | string; startedAt?: Date | string },
  nowMs: number = Date.now(),
  staleMs: number = STEP_HEARTBEAT_STALE_MS,
): boolean {
  if (run.status !== 'running' && run.status !== 'pending') return false;
  const reference = run.heartbeatAt ?? run.updatedAt ?? run.startedAt;
  if (!reference) return true;
  const timestamp = reference instanceof Date ? reference.getTime() : new Date(reference).getTime();
  if (!Number.isFinite(timestamp)) return true;
  return nowMs - timestamp > staleMs;
}

export const DEPARTMENT_WIDE_ADAPTER_KEYS: readonly string[] = DEPARTMENT_WIDE_TIERS.flatMap((t) => t.adapterKeys);

/**
 * @reusable
 * Usage: Dataset tab strip. Hydrants sit before Contacts because Contacts depends on hydrant entities.
 * Rules: Frozen department-wide DAG is unchanged; this is display order only.
 */
export const DATASET_TAB_ORDER: readonly string[] = (() => {
  const keys = [...DEPARTMENT_WIDE_ADAPTER_KEYS];
  const hydrantIdx = keys.indexOf('hydrantMain');
  const contactsIdx = keys.indexOf('occupancyContacts');
  if (hydrantIdx > contactsIdx && contactsIdx >= 0) {
    keys.splice(hydrantIdx, 1);
    keys.splice(contactsIdx, 0, 'hydrantMain');
  }
  return keys;
})();

/**
 * @reusable
 * Usage: Sort GET /datasets + static stubs into operator tab order.
 * Rules: Known occupancy keys first (Hydrants before Contacts); leftover adapters keep relative order at the end.
 */
export function sortAdaptersForMigrationTabs<T extends { key: string }>(adapters: readonly T[]): T[] {
  const rank = new Map(DATASET_TAB_ORDER.map((key, index) => [key, index]));
  return [...adapters].sort((a, b) => {
    const aRank = rank.get(a.key) ?? Number.MAX_SAFE_INTEGER;
    const bRank = rank.get(b.key) ?? Number.MAX_SAFE_INTEGER;
    if (aRank !== bRank) return aRank - bRank;
    return 0;
  });
}

/**
 * @reusable
 * Usage: Documents copy line on the latest-department chip and Documents tab.
 * Rules: presentation-only; never includes SAS, URLs, or filenames.
 */
const REHOST_ERROR_MESSAGES: Record<string, string> = {
  sas_expired: 'Legacy storage SAS expired or returned 403. Rotate LEGACY_STORAGE_SAS_URL and retry Documents.',
  sas_not_configured: 'Legacy storage SAS is not configured on the copy worker. Set LEGACY_STORAGE_SAS_URL and retry.',
  unresolvable_source_url: 'Source file path could not be joined to the legacy storage account. Check att_file_name / hiResURL.',
  unreadable_source: 'Source blob could not be read (empty or inaccessible). Confirm the file exists in Streamline storage.',
  http_404: 'Source blob was not found (404). Confirm the file still exists in Streamline storage.',
  sql_empty: 'SQL binary column was empty. The occupancy photo may only exist as a blob filename.',
  missing_parent: 'Parent occupancy/system record is missing. Migrate Occupancies (and related parents) first.',
  upload_failed: 'Copy to FireSquirrel attachments failed after retries. Check worker logs with the run ID.',
};

/**
 * @reusable
 * Usage: Run History expanded row — translate ledger errorType without SAS/URLs/filenames.
 * Rules: Unknown codes keep a generic copy-failed message plus the raw code.
 */
export function formatRehostErrorType(errorType: string | undefined): string {
  const code = (errorType ?? 'upload_failed').trim() || 'upload_failed';
  return REHOST_ERROR_MESSAGES[code] ?? `File copy failed (${code}).`;
}

export function formatRehostProgressLine(input: {
  rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
  rehostCounts?: RehostCounts;
}): string {
  const counts = input.rehostCounts;
  if (counts && counts.total > 0) {
    const failed = counts.failed > 0 ? ` · ${counts.failed} failed` : '';
    const pending = counts.pending > 0 ? ` · ${counts.pending} pending` : '';
    return `files ${counts.copied}/${counts.total} copied${failed}${pending}`;
  }
  if (input.rehostStatus === 'partial_failed') return 'document re-host partial';
  if (input.rehostStatus === 'not_started' || input.rehostStatus === 'running') {
    return 'document files not copied (metadata linked)';
  }
  return '';
}

export type FileCopyBadgeKind = 'none' | 'copying' | 'not_copied' | 'copied' | 'copy_failed';

/**
 * @reusable
 * Usage: Run History badge for Documents / department-wide apply.
 * Rules: metadata completed is not enough; copy outcome lives on rehostStatus.
 */
export function resolveFileCopyBadge(run: {
  datasetKey?: string;
  isGlobal?: boolean;
  status: string;
  rehostStatus?: 'not_started' | 'running' | 'completed' | 'partial_failed';
  rehostCounts?: RehostCounts;
}): FileCopyBadgeKind {
  const isDocuments = run.datasetKey === 'fileMetadata' || run.datasetKey === 'department-wide' || Boolean(run.isGlobal);
  if (!isDocuments || run.status === 'pending' || run.status === 'running' || run.status === 'cancelled') {
    return 'none';
  }
  if (run.rehostStatus === 'completed') return 'copied';
  if (run.rehostStatus === 'partial_failed') return 'copy_failed';
  if (run.rehostStatus === 'running') return 'copying';
  if (run.status === 'completed') return 'not_copied';
  return 'none';
}

export type DepartmentMigrationCompletenessStatus = 'pending' | 'partial' | 'migrated';

/**
 * @reusable
 * Usage: Left-list badge. Migrated when every occupancy DAG adapter is covered by a
 *        receipt or a completed department-wide / tab apply (empty datasets write no receipts).
 *        Cancelled leftover `skipped` steps are not coverage.
 */
export function departmentMigrationCompleteness(
  receiptDatasetKeys: readonly string[],
  requiredKeys: readonly string[] = DEPARTMENT_WIDE_ADAPTER_KEYS,
  coveredWithoutReceiptKeys: readonly string[] = [],
): { status: DepartmentMigrationCompletenessStatus; coveredKeys: string[]; missingKeys: string[]; receiptCount: number } {
  const covered = new Set([
    ...receiptDatasetKeys.filter(Boolean),
    ...coveredWithoutReceiptKeys.filter(Boolean),
  ]);
  const coveredKeys = requiredKeys.filter((key) => covered.has(key));
  const missingKeys = requiredKeys.filter((key) => !covered.has(key));
  const receiptCount = requiredKeys.filter((key) => receiptDatasetKeys.includes(key)).length;
  let status: DepartmentMigrationCompletenessStatus = 'pending';
  if (coveredKeys.length > 0 && missingKeys.length === 0) status = 'migrated';
  else if (coveredKeys.length > 0) status = 'partial';
  return { status, coveredKeys, missingKeys, receiptCount };
}

/**
 * @reusable
 * Usage: Left-list Partial vs Migrated map from Dexie receipts + runs.
 * Rules: Latest department-wide step keys are the required set. Completed DW
 *        steps and later tab apply runs count as coverage. Skipped leftover
 *        steps after cancel do not.
 */
export function buildDepartmentMigrationCompletenessByTenant(
  receipts: ReadonlyArray<{ tenantId?: string; datasetKey?: string; _deleted?: boolean }>,
  runs: ReadonlyArray<{
    tenantId?: string;
    datasetKey?: string;
    mode?: string;
    status?: string;
    startedAt?: string;
    _deleted?: boolean;
    steps?: Array<{ adapterKey?: string; status?: string }>;
  }>,
): Record<string, DepartmentMigrationCompletenessStatus> {
  const keysByTenant = new Map<string, Set<string>>();
  for (const receipt of receipts) {
    if (receipt._deleted || !receipt.tenantId || !receipt.datasetKey) continue;
    let keys = keysByTenant.get(receipt.tenantId);
    if (!keys) {
      keys = new Set<string>();
      keysByTenant.set(receipt.tenantId, keys);
    }
    keys.add(receipt.datasetKey);
  }

  const coveredByRun = new Map<string, Set<string>>();
  const requiredByTenant = new Map<string, string[]>();
  const latestDwStarted = new Map<string, string>();
  for (const run of runs) {
    if (run._deleted || !run.tenantId) continue;
    let covered = coveredByRun.get(run.tenantId);
    if (!covered) {
      covered = new Set<string>();
      coveredByRun.set(run.tenantId, covered);
    }
    if (run.datasetKey === 'department-wide' && Array.isArray(run.steps)) {
      const startedAt = String(run.startedAt ?? '');
      if (startedAt >= (latestDwStarted.get(run.tenantId) ?? '')) {
        latestDwStarted.set(run.tenantId, startedAt);
        requiredByTenant.set(
          run.tenantId,
          run.steps.map((step) => step?.adapterKey).filter((key): key is string => Boolean(key)),
        );
      }
      for (const step of run.steps) {
        if (step?.adapterKey && step.status === 'completed') covered.add(step.adapterKey);
      }
      continue;
    }
    if (run.mode === 'apply' && run.status === 'completed' && run.datasetKey) {
      covered.add(run.datasetKey);
    }
  }

  const tenantIds = new Set([...keysByTenant.keys(), ...coveredByRun.keys()]);
  const map: Record<string, DepartmentMigrationCompletenessStatus> = {};
  for (const tenantId of tenantIds) {
    map[tenantId] = departmentMigrationCompleteness(
      [...(keysByTenant.get(tenantId) ?? [])],
      requiredByTenant.get(tenantId),
      [...(coveredByRun.get(tenantId) ?? [])],
    ).status;
  }
  return map;
}
